import { createContext, useContext, useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useDeviceSelectors } from "react-device-detect";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import MonitoringCentre from "@/pages/MonitoringCentre";
import InnovationCentre from "@/pages/InnovationCentre";
import HRHub from "@/pages/HRHub";
import LearningHub from "@/pages/LearningHub";
import CourseViewer from "@/pages/CourseViewer";
import TasksHub from "@/pages/TasksHub";
import KnowledgeCentreLayout from "@/pages/KnowledgeCentreLayout";
import InternalMatters from "@/pages/InternalMatters";
import VerifyEmail from "@/pages/VerifyEmail";
import DronesHub from "@/pages/DronesHub";
import DroneViewer from "@/pages/DroneViewer";
import DronePublicViewer from "@/pages/DronePublicViewer";
import SmartDrone from "@/pages/SmartDrone";
import DroneBroadcaster from "@/pages/DroneBroadcaster";
import ThematicMatters from "@/pages/ThematicMatters";
import National from "@/pages/National";
import Continental from "@/pages/Continental";
import Multilaterals from "@/pages/Multilaterals";
import CurrentAffairs from "@/pages/CurrentAffairs";
import KbContacts from "@/pages/KbContacts"
import MonitoredFiles from "@/pages/MonitoredFiles";
import AgentSupport from "@/pages/AgentSupport";
import AdminPanel from "@/pages/AdminPanel";
import PlanManager from "@/pages/PlanManager";
import PricingPage from "@/pages/PricingPage";
import PublicCorporateLanding from "@/pages/PublicCorporateLanding";
import CorporateBilling from "@/pages/CorporateBilling";
import CorporateSettings from "@/pages/CorporateSettings";
import AuthCallback from "@/components/AuthCallback";

// Cyber Monitor
import CyberMonitorLayout from "@/pages/CyberMonitorLayout";
import CyberMonitorHub from "@/pages/CyberMonitorHub";

// eVET Hub
import EVETLayout from "@/pages/EVETLayout";
import EVETHub from "@/pages/EVETHub";

// Smart Office
import SmartOfficeLayout from "@/pages/SmartOfficeLayout";
import SmartFiles from "@/pages/SmartFiles";
import PowerExcel from "@/pages/PowerExcel";
import Translate from "@/pages/Translate";
import Transcribe from "@/pages/Transcribe";
import TranscriptionB from "@/pages/TranscriptionB";
import DocConverter from "@/pages/DocConverter";
import WorkDocs from "@/pages/WorkDocs";
import AIEditorHub from "@/pages/AIEditorHub";
import AiAuthenticator from "@/pages/AiAuthenticator";

// IMS Hub
import ImsHubLayout from "@/pages/ImsHubLayout";
import ImsIngest from "@/pages/ImsIngest";
import ImsFacts from "@/pages/ImsFacts";
import ImsReports from "@/pages/ImsReports";
import ImsQuery from "@/pages/ImsQuery";

// Smart Vetting
import SmartVettingLayout from "@/pages/SmartVettingLayout";

// Logistics & SCM Hub
import LogisticsHubLayout from "@/pages/LogisticsHubLayout";
import LogisticsInventory from "@/pages/LogisticsInventory";
import LogisticsOffices from "@/pages/LogisticsOffices";
import LogisticsFleet from "@/pages/LogisticsFleet";
import LogisticsSCM from "@/pages/LogisticsSCM";

// Travelling Admin Hub
import TravellingAdminLayout from "@/pages/TravellingAdminLayout";
import TravellingAdminMyTrips from "@/pages/TravellingAdminMyTrips";
import TravellingAdminTravelDesk from "@/pages/TravellingAdminTravelDesk";
import TravellingAdminManager from "@/pages/TravellingAdminManager";
import TravellingAdminFinance from "@/pages/TravellingAdminFinance";
import TravellingAdminSuspicious from "@/pages/TravellingAdminSuspicious";
import TravellingAdminCurrency from "@/pages/TravellingAdminCurrency";
import VettingDashboard from "@/pages/VettingDashboard";
import VettingNewCase from "@/pages/VettingNewCase";
import VettingCaseDetail from "@/pages/VettingCaseDetail";
import VettingAdmin from "@/pages/VettingAdmin";

// Request Service Hub
import RequestServiceLayout from "@/pages/RequestServiceLayout";
import RequestServiceDashboard from "@/pages/RequestServiceDashboard";
import RequestServiceNewRequest from "@/pages/RequestServiceNewRequest";
import RequestServiceMyRequests from "@/pages/RequestServiceMyRequests";
import RequestServiceMyAssigned from "@/pages/RequestServiceMyAssigned";
import RequestServiceServiceDesk from "@/pages/RequestServiceServiceDesk";
import RequestServiceRequestDetail from "@/pages/RequestServiceRequestDetail";
import RequestServiceMedicalCalendar from "@/pages/RequestServiceMedicalCalendar";
import RequestServiceITInventory from "@/pages/RequestServiceITInventory";
import RequestServiceOfficeSupply from "@/pages/RequestServiceOfficeSupply";
import RequestServiceReports from "@/pages/RequestServiceReports";

// HR Hub
import HRHubLayout from "@/pages/HRHubLayout";
import HumanCentre from "@/pages/HumanCentre";
import OurOrganisation from "@/pages/OurOrganisation";
import HRAdminPanel from "@/pages/HRAdminPanel";
import HRIntelligence from "@/pages/HRIntelligence";
import PerformanceApp from "@/pages/PerformanceApp";

// Smart Management System
import SmartManagementLayout from "@/pages/SmartManagementLayout";
import SMSDashboard from "@/pages/SMSDashboard";
import SMSCreateDocument from "@/pages/SMSCreateDocument";
import SMSDocumentView from "@/pages/SMSDocumentView";
import SMSAdmin from "@/pages/SMSAdmin";

// Leave Management System
import LeaveManagementLayout from "@/pages/LeaveManagementLayout";
import LMSDashboard from "@/pages/LMSDashboard";
import LMSApply from "@/pages/LMSApply";
import LMSMyLeave from "@/pages/LMSMyLeave";
import LMSApprovals from "@/pages/LMSApprovals";
import LMSCalendar from "@/pages/LMSCalendar";
import LMSCapacity from "@/pages/LMSCapacity";
import LMSAdmin from "@/pages/LMSAdmin";

// ERP Hub
import ERPLayout from "@/pages/ERPLayout";
import ERPHub from "@/pages/ERPHub";
import ERPFinance from "@/pages/ERPFinance";
import ERPSCM from "@/pages/ERPSCM";
import ERPProduction from "@/pages/ERPProduction";
import ERPAssistant from "@/pages/ERPAssistant";
import ERPDocuments from "@/pages/ERPDocuments";

// SmartPay Hub
import SmartPayLayout from "@/pages/SmartPayLayout";
import SmartPayHub from "@/pages/SmartPayHub";
import SmartPayContractors from "@/pages/SmartPayContractors";
import SmartPayHRIS from "@/pages/SmartPayHRIS";
import SmartPayEquipment from "@/pages/SmartPayEquipment";
import SmartPayAssistant from "@/pages/SmartPayAssistant";
import SmartPayPayroll from "@/pages/SmartPayPayroll";
import SmartPayEOR from "@/pages/SmartPayEOR";

// Comms Centre
import CommsCentreLayout from "@/pages/CommsCentreLayout";
import Chat from "@/pages/Chat";
import GroupChat from "@/pages/GroupChat";
import Meetings from "@/pages/Meetings";
import TasksManager from "@/pages/TasksManager";
import Calls from "@/pages/Calls";
import Notifications from "@/pages/Notifications";
import AIPals from "@/pages/AIPals";
import Broadcast from "@/pages/Broadcast";
import MailLayout from "@/pages/MailLayout";
import MailInbox from "@/pages/MailInbox";
import MailDetail from "@/pages/MailDetail";
import MailContacts from "@/pages/MailContacts";
import MailCalendar from "@/pages/MailCalendar";
import MailTasks from "@/pages/MailTasks";
import MailSettings from "@/pages/MailSettings";
import MailAgent from "@/pages/MailAgent";
import TolokySettings from "@/pages/TolokySettings";
import NoterHub from "@/pages/NoterHub";
import AssemblyHub from "@/pages/AssemblyHub";
import SmartTalk from "@/pages/SmartTalk";
import CorporateHub from "@/pages/CorporateHub";
import PeopleDirectory from "@/pages/PeopleDirectory";
import PersonProfile from "@/pages/PersonProfile";
import OrgProfilePage from "@/pages/OrgProfilePage";
import AllBaiseProfile from "@/pages/AllBaiseProfile";
import TestProfile from "@/pages/TestProfile";
import DealsHub from "@/pages/DealsHub";
import CareerHub from "@/pages/CareerHub"
import AgentsCentre from "@/pages/AgentsCentre";
import WishCentre from "@/pages/WishCentre";
import BottomNav from "@/components/BottomNav";

// Alerts & Rewards
import AlertsHub from "@/pages/AlertsHub";
import AllAlertsPage from "@/pages/AllAlertsPage";
import RewardsCentre from "@/pages/RewardsCentre";
import NoticeBoard from "@/pages/NoticeBoard";

// Aipps Credits
import Aipps from "@/pages/Aipps";
import PaymentSuccess from "@/pages/PaymentSuccess";
import i18n from "@/services/i18nService";
import { TProvider } from "@/components/common/Trans";
import { HubAccessProvider } from "@/services/hubAccess";

// Components
import AllBaisePal from "@/components/AllBaisePal";

const API = (() => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "http://localhost:8001/api";
  return `https://api.${host}/api`;
})();

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  const checkAuth = useCallback(async () => {
    // Skip if OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` },
          withCredentials: true
        });
        setUser(response.data);
        setToken(storedToken);
        setLoading(false);
        return;
      } catch (e) {
        localStorage.removeItem("token");
        setToken(null);
      }
    }
    
    // Try cookie-based auth
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch {
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    localStorage.setItem("token", response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const setAuthUser = (userData, authToken) => {
    setUser(userData);
    if (authToken) {
      localStorage.setItem("token", authToken);
      setToken(authToken);
    }
  };

  const value = { user, loading, token, login, register, logout, checkAuth, setAuthUser };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Protected Route
function ProtectedRoute({ children, requiredPermission }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Check feature permission if required
  if (requiredPermission && user.role !== "admin") {
    const permissions = user.permissions || {};
    if (permissions[requiredPermission] === false) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
}

// Smart Root — shows Landing if not authenticated, Profile if authenticated
function LandingOrProfile() {
  const { user, loading } = useAuth();
  const [redirect, setRedirect] = useState(null);

  useEffect(() => {
    if (loading || !user) return;
    if (user.account_type === "corporate" || user.role === "TENANT_ADMIN") {
      const token = localStorage.getItem("token");
      axios.get(API + "/auth/my-org", { headers: token ? { Authorization: "Bearer " + token } : {} })
        .then(res => {
          const org = res.data.org;
          if (org && org.subdomain) {
            setRedirect("/corporate-hub/" + org.subdomain);
          } else if (org && org.id) {
            setRedirect("/corporate-hub/" + org.id);
          } else if (user.username) {
            // Fallback to username — the org should have been auto-created
            setRedirect("/corporate-hub/" + user.username);
          } else {
            setRedirect("/corporate-hub");
          }
        })
        .catch(() => {
          // If my-org call fails, try username as subdomain
          if (user.username) {
            setRedirect("/corporate-hub/" + user.username);
          } else {
            setRedirect("/corporate-hub");
          }
        });
    } else if (user.username) {
      // Personal user: redirect to /people/{username} like Facebook/LinkedIn
      setRedirect("/people/" + user.username);
    } else {
      setRedirect("/");
    }
  }, [user, loading]);

  if (loading) return <div className="min-h-screen bg-[#0f0f13] flex items-center justify-center"><div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Landing />;
  if (!redirect) return <div className="min-h-screen bg-[#0f0f13] flex items-center justify-center"><div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (redirect.startsWith("/people") || redirect.startsWith("/corporate-hub")) {
    return <Navigate to={redirect} replace />;
  }
  return <AllBaiseProfile />;
}

// Admin Route — requires admin role
function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Main App Router
function AppRouter() {
  const location = useLocation();
  const auth = useAuth();
  
  // Check for OAuth callback - synchronous detection
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<LandingOrProfile />} />
        <Route path="/landing" element={<Landing />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/corporate/:subdomain" element={<PublicCorporateLanding />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/dashboard" element={
        <ProtectedRoute requiredPermission="dashboard">
          <Dashboard />
        </ProtectedRoute>
      } />

      {/* Smart Office with nested routes */}
      <Route path="/smart-office" element={
        <ProtectedRoute requiredPermission="smart_office">
          <SmartOfficeLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/smart-office/files" replace />} />
        <Route path="files" element={<SmartFiles />} />
        <Route path="excel" element={<PowerExcel />} />
        <Route path="translate" element={<Translate />} />
        <Route path="transcribe" element={<Transcribe />} />
        <Route path="transcribe-b" element={<TranscriptionB />} />
        <Route path="transcribe-d" element={<Navigate to="/smart-office/transcribe-b" replace />} />
        <Route path="ai-editor" element={<AIEditorHub />} />
        <Route path="ai-authenticator" element={<AiAuthenticator />} />
        <Route path="doc-converter" element={<DocConverter />} />
        <Route path="workdocs" element={<WorkDocs />} />
      </Route>

      {/* IMS Hub with nested routes */}
      <Route path="/ims" element={
        <ProtectedRoute requiredPermission="ims">
          <ImsHubLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/ims/ingest" replace />} />
        <Route path="ingest" element={<ImsIngest />} />
        <Route path="facts" element={<ImsFacts />} />
        <Route path="reports" element={<ImsReports />} />
        <Route path="raw" element={<ImsQuery />} />
      </Route>

      {/* Logistics & SCM Hub with nested routes */}
      <Route path="/logistics" element={
        <ProtectedRoute requiredPermission="logistics">
          <LogisticsHubLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/logistics/inventory" replace />} />
        <Route path="inventory" element={<LogisticsInventory />} />
        <Route path="offices" element={<LogisticsOffices />} />
        <Route path="fleet" element={<LogisticsFleet />} />
        <Route path="scm" element={<LogisticsSCM />} />
      </Route>

      {/* Travelling Admin Hub with nested routes */}
      <Route path="/travelling" element={
        <ProtectedRoute requiredPermission="travelling">
          <TravellingAdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/travelling/my-trips" replace />} />
        <Route path="my-trips" element={<TravellingAdminMyTrips />} />
        <Route path="travel-desk" element={<TravellingAdminTravelDesk />} />
        <Route path="manager" element={<TravellingAdminManager />} />
        <Route path="finance" element={<TravellingAdminFinance />} />
        <Route path="suspicious" element={<TravellingAdminSuspicious />} />
        <Route path="currency" element={<TravellingAdminCurrency />} />
      </Route>

      {/* Request Service Hub with nested routes */}
      <Route path="/request-service" element={
        <ProtectedRoute requiredPermission="request_service">
          <RequestServiceLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/request-service/dashboard" replace />} />
        <Route path="dashboard" element={<RequestServiceDashboard />} />
        <Route path="new-request" element={<RequestServiceNewRequest />} />
        <Route path="my-requests" element={<RequestServiceMyRequests />} />
        <Route path="my-requests/:id" element={<RequestServiceRequestDetail />} />
        <Route path="my-assigned" element={<RequestServiceMyAssigned />} />
        <Route path="service-desk" element={<RequestServiceServiceDesk />} />
        <Route path="medical-calendar" element={<RequestServiceMedicalCalendar />} />
        <Route path="it-inventory" element={<RequestServiceITInventory />} />
        <Route path="office-supply" element={<RequestServiceOfficeSupply />} />
        <Route path="reports" element={<RequestServiceReports />} />
      </Route>

      {/* Smart Vetting with nested routes */}
      <Route path="/vetting" element={
        <ProtectedRoute requiredPermission="vetting">
          <SmartVettingLayout />
        </ProtectedRoute>
      }>
        <Route index element={<VettingDashboard />} />
        <Route path="new" element={<VettingNewCase />} />
        <Route path="admin" element={<VettingAdmin />} />
        <Route path=":caseId" element={<VettingCaseDetail />} />
      </Route>

      {/* ERP Hub with nested routes */}
      <Route path="/erp" element={
        <ProtectedRoute requiredPermission="smart_office">
          <ERPLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/erp/dashboard" replace />} />
        <Route path="dashboard" element={<ERPHub />} />
        <Route path="finance" element={<ERPFinance />} />
        <Route path="scm" element={<ERPSCM />} />
        <Route path="production" element={<ERPProduction />} />
        <Route path="assistant" element={<ERPAssistant />} />
        <Route path="documents" element={<ERPDocuments />} />
      </Route>

      {/* SmartPay Hub with nested routes */}
      <Route path="/smartpay" element={
        <ProtectedRoute requiredPermission="smartpay">
          <SmartPayLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/smartpay/dashboard" replace />} />
        <Route path="dashboard" element={<SmartPayHub />} />
        <Route path="contractors" element={<SmartPayContractors />} />
        <Route path="hris" element={<SmartPayHRIS />} />
        <Route path="equipment" element={<SmartPayEquipment />} />
        <Route path="payroll" element={<SmartPayPayroll />} />
        <Route path="eor" element={<SmartPayEOR />} />
        <Route path="assistant" element={<SmartPayAssistant />} />
      </Route>

      <Route path="/monitoring-centre" element={
        <ProtectedRoute requiredPermission="monitoring">
          <MonitoringCentre />
        </ProtectedRoute>
      } />
      <Route path="/innovation-centre" element={
        <ProtectedRoute requiredPermission="innovation">
          <InnovationCentre />
        </ProtectedRoute>
      } />
      <Route path="/cyber-monitor" element={
        <ProtectedRoute requiredPermission="cyber_monitor">
          <CyberMonitorLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/cyber-monitor/dashboard" replace />} />
        <Route path="dashboard" element={<CyberMonitorHub />} />
        <Route path="entities" element={<CyberMonitorHub />} />
        <Route path="graph" element={<CyberMonitorHub />} />
        <Route path="monitors" element={<CyberMonitorHub />} />
        <Route path="cases" element={<CyberMonitorHub />} />
        <Route path="evidence" element={<CyberMonitorHub />} />
        <Route path="ai" element={<CyberMonitorHub />} />
      </Route>
      {/* eVET Hub with nested routes */}
      <Route path="/evet" element={
        <ProtectedRoute requiredPermission="evet">
          <EVETLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/evet/dashboard" replace />} />
        <Route path="dashboard" element={<EVETHub />} />
        <Route path="collector" element={<EVETHub />} />
        <Route path="screener" element={<EVETHub />} />
        <Route path="interrogator" element={<EVETHub />} />
        <Route path="connector" element={<EVETHub />} />
        <Route path="clerk" element={<EVETHub />} />
        <Route path="sentinel" element={<EVETHub />} />
        <Route path="audit" element={<EVETHub />} />
      </Route>

      {/* HR Hub with nested routes */}
      <Route path="/hr-hub" element={
        <ProtectedRoute requiredPermission="hr">
          <HRHubLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/hr-hub/human-centre" replace />} />
        <Route path="human-centre" element={<HumanCentre />} />
        <Route path="our-organisation" element={<OurOrganisation />} />
        <Route path="hr-admin" element={<HRAdminPanel />} />
        <Route path="hr-intelligence" element={<HRIntelligence />} />
        <Route path="performance" element={<PerformanceApp />} />
        <Route path="smart-management" element={<SmartManagementLayout />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<SMSDashboard />} />
          <Route path="create" element={<SMSCreateDocument />} />
          <Route path=":docId" element={<SMSDocumentView />} />
          <Route path="admin" element={<SMSAdmin />} />
        </Route>
        <Route path="leave-management" element={<LeaveManagementLayout />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<LMSDashboard />} />
          <Route path="request" element={<LMSApply />} />
          <Route path="my-leave" element={<LMSMyLeave />} />
          <Route path="approvals" element={<LMSApprovals />} />
          <Route path="calendar" element={<LMSCalendar />} />
          <Route path="capacity" element={<LMSCapacity />} />
          <Route path="admin" element={<LMSAdmin />} />
        </Route>
      </Route>

      {/* Comms Centre with nested routes */}
      <Route path="/comms-centre" element={
        <ProtectedRoute requiredPermission="comms">
          <CommsCentreLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/comms-centre/chat" replace />} />
        <Route path="chat" element={<Chat />} />
        <Route path="groups" element={<GroupChat />} />
        <Route path="calls" element={<Calls />} />
        <Route path="meetings" element={<Meetings />} />
        <Route path="tasks" element={<TasksManager />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="ai-pals" element={<AIPals />} />
        <Route path="broadcast" element={<Broadcast />} />
        <Route path="mail" element={<MailLayout />}>
          <Route index element={<Navigate to="inbox" replace />} />
          <Route path="inbox" element={<MailInbox />} />
          <Route path="starred" element={<MailInbox />} />
          <Route path="sent" element={<MailInbox />} />
          <Route path="drafts" element={<MailInbox />} />
          <Route path="archive" element={<MailInbox />} />
          <Route path="trash" element={<MailInbox />} />
          <Route path="detail/:emailId" element={<MailDetail />} />
          <Route path="contacts" element={<MailContacts />} />
          <Route path="calendar" element={<MailCalendar />} />
          <Route path="tasks" element={<MailTasks />} />
          <Route path="settings" element={<MailSettings />} />
          <Route path="agent" element={<MailAgent />} />
        </Route>
        <Route path="toloky" element={<TolokySettings />} />
        <Route path="noter" element={<NoterHub />} />
        <Route path="smartmail/*" element={<Navigate to="/comms-centre/mail" replace />} />
      </Route>

      <Route path="/assembly-hub" element={<ProtectedRoute><AssemblyHub /></ProtectedRoute>} />
      <Route path="/smart-talk" element={<ProtectedRoute><SmartTalk /></ProtectedRoute>} />
      <Route path="/corporate-hub" element={<ProtectedRoute><CorporateHub /></ProtectedRoute>} />
      <Route path="/corporate-hub/:orgId" element={<ProtectedRoute><OrgProfilePage /></ProtectedRoute>} />
      <Route path="/corporate-hub/settings/billing" element={<ProtectedRoute><CorporateBilling /></ProtectedRoute>} />
      <Route path="/corporate-hub/settings" element={<ProtectedRoute><CorporateSettings /></ProtectedRoute>} />
      <Route path="/people-directory" element={<ProtectedRoute><PeopleDirectory /></ProtectedRoute>} />
      <Route path="/people/:userId" element={<PersonProfile />} />
      <Route path="/test-profile/:userId" element={<TestProfile />} />
      <Route path="/deals" element={<ProtectedRoute><DealsHub /></ProtectedRoute>} />
      <Route path="/career" element={<ProtectedRoute><CareerHub /></ProtectedRoute>} />
      <Route path="/wish-centre" element={<ProtectedRoute><WishCentre /></ProtectedRoute>} />
      <Route path="/agents-centre" element={<ProtectedRoute><AgentsCentre /></ProtectedRoute>} />
      
      {/* Legacy HR route - redirect to new HR Hub */}
      <Route path="/hr" element={<Navigate to="/hr-hub/human-centre" replace />} />
      <Route path="/learning" element={
        <ProtectedRoute requiredPermission="learning">
          <LearningHub />
        </ProtectedRoute>
      } />
      <Route path="/course/:id" element={
        <ProtectedRoute requiredPermission="courses">
          <CourseViewer />
        </ProtectedRoute>
      } />
      {/* Legacy routes - redirect to Comms Centre */}
      <Route path="/tasks" element={<Navigate to="/comms-centre/tasks" replace />} />

      <Route path="/knowledge" element={<Navigate to="/knowledge-centre" replace />} />

      <Route path="/knowledge-centre" element={
        <ProtectedRoute requiredPermission="knowledge">
          <KnowledgeCentreLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="internal-matters" replace />} />
        <Route path="internal-matters" element={<InternalMatters />} />
        <Route path="thematic-matters" element={<ThematicMatters />} />
        <Route path="national" element={<National />} />
        <Route path="continental" element={<Continental />} />
        <Route path="multilaterals" element={<Multilaterals />} />
        <Route path="current-affairs" element={<CurrentAffairs />} />
        <Route path="contacts" element={<KbContacts />} />
        <Route path="monitored-files" element={<MonitoredFiles />} />
      </Route>
      <Route path="/agent" element={
        <ProtectedRoute requiredPermission="agent">
          <AgentSupport />
        </ProtectedRoute>
      } />

      {/* Admin Panel */}
      <Route path="/admin" element={
        <AdminRoute>
          <AdminPanel />
        </AdminRoute>
      } />

      {/* Admin: Subscription Plans */}
      <Route path="/admin/plans" element={
        <AdminRoute>
          <PlanManager />
        </AdminRoute>
      } />

      {/* Aipps Credits */}
      <Route path="/aipps" element={<Aipps />} />
      <Route path="/payment-success" element={
        <ProtectedRoute>
          <PaymentSuccess />
        </ProtectedRoute>
      } />
      
      {/* Alerts Hub */}
      <Route path="/alerts" element={
        <ProtectedRoute requiredPermission="alerts">
          <AlertsHub />
        </ProtectedRoute>
      } />

      {/* All Alerts — community incident reporting */}
      <Route path="/all-alerts" element={<AllAlertsPage />} />

      {/* Notice Board */}
      <Route path="/notice-board" element={<NoticeBoard />} />

      {/* Rewards Centre */}
      <Route path="/drones" element={<DronesHub />} />
      <Route path="/drones/broadcast" element={<DroneBroadcaster />} />
      <Route path="/drones/stream/:roomId" element={<DroneViewer />} />
      <Route path="/drone/view/:token" element={<DronePublicViewer />} />
      <Route path="/smart-drone" element={<ProtectedRoute><SmartDrone /></ProtectedRoute>} />
      <Route path="/rewards" element={
        <ProtectedRoute requiredPermission="rewards">
          <RewardsCentre />
        </ProtectedRoute>
      } />
    </Routes>
      {auth.user && <AllBaisePal />}
      {auth.user && <BottomNav />}
    </>
  );
}

function App() {
  const { isMobile, isTablet, isDesktop, isIOS, isAndroid, isPortrait, isLandscape } = useDeviceSelectors();
  const [, forceRerender] = useState(0);

  // Initialize i18n on mount + subscribe to changes
  useEffect(() => {
    i18n.init();
    const unsub = i18n.onChange(() => forceRerender(n => n + 1));
    return unsub;
  }, []);

  const t = useCallback((key, params) => i18n.t(key, params), [forceRerender]);

  useEffect(() => {
    const html = document.documentElement;

    // Remove existing device classes
    html.classList.remove("mobile", "tablet", "desktop", "ios", "android", "portrait", "landscape");

    // Apply device type class
    if (isMobile) html.classList.add("mobile");
    if (isTablet) html.classList.add("tablet");
    if (isDesktop) html.classList.add("desktop");
    if (isIOS) html.classList.add("ios");
    if (isAndroid) html.classList.add("android");
    if (isPortrait) html.classList.add("portrait");
    if (isLandscape) html.classList.add("landscape");

    // Dev-mode debug log
    if (process.env.NODE_ENV === "development") {
      console.log("[O-BAISE Device Detect]", {
        isMobile,
        isTablet,
        isDesktop,
        isIOS,
        isAndroid,
        isPortrait,
        isLandscape,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      });
    }
  }, [isMobile, isTablet, isDesktop, isIOS, isAndroid, isPortrait, isLandscape]);

  function AuthAwareApp() {
    const auth = useAuth();
    return (
      <HubAccessProvider user={auth.user}>
        <AppRouter />
      </HubAccessProvider>
    );
  }

  return (
    <BrowserRouter>
      <AuthProvider>
        <TProvider t={t}>
          <Toaster position="top-right" richColors />
          <AuthAwareApp />
        </TProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
